import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateDocumentTypeRateRequest,
  DeleteDocumentTypeRateRequest,
  UpdateDocumentTypeRateRequest,
} from './DocumentTypeRates.types';

import {
  listDocumentTypeRates,
  deleteDocumentTypeRate,
  createDocumentTypeRate,
  updateDocumentTypeRate,
} from './DocumentTypeRates.service';
import { useIntl } from 'react-intl';
import { notification } from 'antd';

export function useDocumentTypeRates(documentTypeId?: number) {
  const query = useInfiniteQuery(
    ['documentTypeRates', documentTypeId],
    () => {
      return listDocumentTypeRates(documentTypeId);
    },
    {
      enabled: !!documentTypeId,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateDocumentTypeRate(documentTypeId: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreateDocumentTypeRateRequest[]) =>
      createDocumentTypeRate(documentTypeId, obj),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'documentTypes.rate.modal.create.success',
          }),
          description: intl.formatMessage({
            id: 'documentTypes.rate.modal.create.successDescription',
          }),
        });

        await queryClient.invalidateQueries([
          'documentTypeRates',
          documentTypeId,
        ]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateDocumentTypeRate(documentTypeId: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateDocumentTypeRateRequest) =>
      updateDocumentTypeRate(documentTypeId, obj),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'documentTypes.rate.modal.update.success',
          }),
          description: intl.formatMessage({
            id: 'documentTypes.rate.modal.update.successDescription',
          }),
        });

        await queryClient.getMutationCache().clear();
        await queryClient.invalidateQueries(['documentTypes']);
        await queryClient.invalidateQueries([
          'documentTypeRates',
          documentTypeId,
        ]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteDocumentTypeRate(documentTypeId: number) {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: DeleteDocumentTypeRateRequest) =>
      deleteDocumentTypeRate(documentTypeId, obj),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'documentTypes.rate.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'documentTypes.rate.remove.notification.successDescription',
          }),
        });

        await queryClient.invalidateQueries([
          'documentTypeRates',
          documentTypeId,
        ]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
